/**
 * Step 1 - Upload do arquivo CSV.
 *
 * Recebe o arquivo via input/drag, valida tamanho/limites e devolve
 * headers + rows ao orquestrador. O nome do job é definido aqui também.
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.2.0 - Adicionada seção colapsável "Origens
 *            aceitas" listando os 19 valores válidos para a coluna
 *            `origem` (consumidos direto de LEAD_SOURCES para evitar
 *            divergência com a validação do backend).
 *   RELEASE: 2026-05-16 v1.1.0 - Botão "Baixar CSV modelo" para auxiliar o
 *            usuário com um arquivo de exemplo já no formato esperado.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { useState } from 'react';
import { Upload, Download, ChevronDown, Tag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { downloadSampleDealsCsv } from '../lib/sample-csv';
import { LEAD_SOURCES, LEAD_SOURCE_ICONS } from '@/lib/lead-sources';

interface Step1UploadProps {
  jobName: string;
  onJobNameChange: (v: string) => void;
  onFileSelected: (file: File) => void;
}

export function Step1Upload({ jobName, onJobNameChange, onFileSelected }: Step1UploadProps) {
  // Colapsável fechado por padrão para não poluir a tela inicial
  const [openSources, setOpenSources] = useState(false);

  return (
    <div className="space-y-4">
      <div>
        <Label>Nome da importação</Label>
        <Input
          value={jobName}
          onChange={(e) => onJobNameChange(e.target.value)}
          placeholder="Ex.: Leads Outubro"
          maxLength={120}
        />
      </div>

      <label
        htmlFor="csv-upload"
        className="block border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-muted/50 transition"
      >
        <Upload className="mx-auto h-10 w-10 mb-3 text-muted-foreground" />
        <p className="font-medium">Clique para selecionar o CSV</p>
        <p className="text-xs text-muted-foreground mt-1">
          Máx. 50.000 linhas / 10 MB
        </p>
        <input
          id="csv-upload"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            // Reset imediato do input para permitir reselecionar o MESMO arquivo
            // após um erro (ex.: parser rejeitou) — sem isso o onChange não dispara.
            e.target.value = '';
            if (f) onFileSelected(f);
          }}
        />
      </label>

      {/* Atalho para baixar CSV modelo com cabeçalho e exemplos válidos */}
      <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-4">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">Não sabe por onde começar?</p>
          <p className="text-xs text-muted-foreground">
            Baixe um CSV modelo com cabeçalho e 3 linhas de exemplo já no formato esperado.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => downloadSampleDealsCsv()}
        >
          <Download className="h-4 w-4 mr-2" />
          Baixar CSV modelo
        </Button>
      </div>

      {/*
        Tabela de origens aceitas pelo sistema.
        Consome direto LEAD_SOURCES para garantir 1-a-1 com a validação
        do backend (qualquer valor fora dessa lista é rejeitado no Step 4).
      */}
      <Collapsible open={openSources} onOpenChange={setOpenSources}>
        <div className="rounded-lg border">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">
                  Origens aceitas no campo <code className="text-xs bg-muted px-1 py-0.5 rounded">origem</code> ({LEAD_SOURCES.length})
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${openSources ? 'rotate-180' : ''}`}
              />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="border-t p-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Use <strong>exatamente</strong> um destes valores (minúsculo, sem espaços) na
                coluna <code className="text-[11px] bg-muted px-1 py-0.5 rounded">origem</code> do
                CSV. Valores diferentes serão rejeitados na etapa de validação.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {LEAD_SOURCES.map((src) => {
                  const Icon = LEAD_SOURCE_ICONS[src.id];
                  return (
                    <div
                      key={src.id}
                      className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs"
                    >
                      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <code className="font-mono text-foreground truncate">{src.id}</code>
                      <span className="text-muted-foreground truncate ml-auto">
                        {src.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
